/**
 * WidgetCatalog — modal for browsing and adding dashboard widgets.
 */

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
} from '@/components/ui'
import { getWidgetsByCategory } from './widgetRegistry'
import type { WidgetRegistryEntry, WidgetCategory } from '@/types/dashboard'
import {
  Hash,
  Filter,
  TrendingUp,
  BarChart3,
  GitBranch,
  Grid3x3,
  Route,
  Radar,
  ScatterChart,
  Megaphone,
  Sparkles,
  Table,
  Plus,
} from 'lucide-react'

/** Map icon name strings to lucide components */
const ICON_MAP: Record<string, any> = {
  Hash, Filter, TrendingUp, BarChart3, GitBranch, Grid3x3,
  Route, Radar, ScatterChart, Megaphone, Sparkles, Table,
}

const CATEGORY_LABELS: Record<WidgetCategory, string> = {
  metrics: 'Metrics',
  charts: 'Charts & Visualizations',
  tables: 'Tables & Lists',
  insights: 'AI Insights',
}

const CATEGORY_ORDER: WidgetCategory[] = ['metrics', 'charts', 'tables', 'insights']

interface WidgetCatalogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAddWidget: (entry: WidgetRegistryEntry) => void
}

export function WidgetCatalog({ open, onOpenChange, onAddWidget }: WidgetCatalogProps) {
  const grouped = getWidgetsByCategory()

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Add Widget</ModalTitle>
          <ModalDescription>Choose a widget to add to your dashboard</ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-6">
            {CATEGORY_ORDER.map((category) => {
              const entries = grouped[category]
              if (!entries || entries.length === 0) return null
              return (
                <div key={category}>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {CATEGORY_LABELS[category]}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {entries.map((entry) => {
                      const Icon = ICON_MAP[entry.icon] || Hash
                      return (
                        <button
                          key={entry.type}
                          onClick={() => {
                            onAddWidget(entry)
                            onOpenChange(false)
                          }}
                          className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
                        >
                          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted group-hover:bg-primary/20 transition-colors shrink-0">
                            <Icon className="w-4.5 h-4.5 text-muted-foreground group-hover:text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{entry.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{entry.description}</p>
                          </div>
                          <Plus className="w-4 h-4 text-muted-foreground group-hover:text-primary mt-0.5 shrink-0 ml-auto" />
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
